import { useEffect, useState } from "react";
import { Navigate, NavLink, useParams } from "react-router-dom";
import { Bell, Check, CreditCard, Lock, Palette, ShieldAlert, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/app/AppShell";
import DeleteAccountPanel, { DeletionLists } from "@/components/app/DeleteAccount";
import ProfileView from "@/pages/ProfileView";
import Wallet from "@/pages/Wallet";
import {
  DEFAULT_THEME,
  getThemePreference,
  getUser,
  isWinterArcEligible,
  setThemePreference,
  WINTER_ARC_THEME,
} from "@/lib/storage";
import { getNotificationPreferences, updateNotificationPreferences } from "@/lib/api";
import { currentPushState, disableWebPush, enableWebPush } from "@/lib/pushNotifications";

const sections = [
  { key: "profile", label: "Profile", icon: UserIcon },
  { key: "membership", label: "Membership", icon: CreditCard },
  { key: "themes", label: "Themes", icon: Palette },
  { key: "notifications", label: "Notifications", icon: Bell },
  { key: "account", label: "Account", icon: ShieldAlert },
];

export default function Settings() {
  const { section } = useParams();
  if (!sections.some((item) => item.key === section)) {
    return <Navigate to="/settings/profile" replace />;
  }

  return (
    <AppShell>
      <div className="border-b border-white/5 px-6 sm:px-10 pt-9">
        <div className="max-w-5xl mx-auto">
          <div className="overline mb-2">Account settings</div>
          <h1 className="font-serif-editorial text-4xl mb-7">Settings</h1>
          <nav className="flex gap-1 overflow-x-auto" aria-label="Settings sections">
            {sections.map((item) => (
              <NavLink
                key={item.key}
                to={`/settings/${item.key}`}
                className={({ isActive }) => `inline-flex items-center gap-2 border-b-2 px-4 py-3 text-sm whitespace-nowrap transition-colors ${
                  isActive
                    ? "border-[#F59E0B] text-[#F5F5F0]"
                    : "border-transparent text-[#A1A1AA] hover:text-[#F5F5F0]"
                }`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>
      {section === "profile" && <ProfileView embedded />}
      {section === "membership" && <Wallet embedded />}
      {section === "themes" && <ThemesSettings />}
      {section === "notifications" && <NotificationSettings />}
      {section === "account" && <AccountSettings />}
    </AppShell>
  );
}

function AccountSettings() {
  return (
    <div className="mx-auto max-w-5xl px-6 py-10 sm:px-10">
      <div className="mb-8">
        <div className="overline mb-3">Your data</div>
        <h2 className="font-serif-editorial text-4xl">Delete your account.</h2>
        <p className="mt-3 max-w-2xl text-sm text-[#A1A1AA]">Deleting is immediate and works the same from the website and the Android app. Here is exactly what goes and what stays.</p>
      </div>
      <DeletionLists />
      <div className="mt-6"><DeleteAccountPanel /></div>
    </div>
  );
}

const preferenceRows = [
  ["newMatches", "New matches", "When a like becomes a match."],
  ["incomingLikes", "Incoming likes", "When someone likes your profile."],
  ["likeResets", "Daily like reset", "When your daily likes become available again."],
  ["nudges", "Partner nudges", "When your accountability partner nudges a task."],
  ["partnerTaskCompletions", "Partner task completions", "When your partner marks a tracked task complete."],
  ["chatMessages", "Chat messages", "When a match sends you a new message."],
  ["taskReminders", "Task reminders", "Before a task with a set start time."],
  ["meetingReminders", "Meet reminders", "When a meet is scheduled and shortly before it starts."],
  ["dailySummaries", "End-of-day summary", "A concise completed-versus-pending task count."],
];

function NotificationSettings() {
  const [preferences, setPreferences] = useState(null);
  const [push, setPush] = useState({ supported: true, subscribed: false, permission: "default" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getNotificationPreferences().then(setPreferences).catch((error) => toast.error(error.message));
    currentPushState().then(setPush).catch(() => setPush({ supported: false, subscribed: false, permission: "unsupported" }));
  }, []);

  const togglePush = async () => {
    setSaving(true);
    try {
      const state = push.subscribed ? await disableWebPush() : await enableWebPush();
      setPush(state);
      toast.success(state.subscribed ? "Device notifications enabled." : "Device notifications disabled.");
    } catch (error) { toast.error(error.message); } finally { setSaving(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { adminAnnouncements, ...editable } = preferences;
      const updated = await updateNotificationPreferences(editable);
      setPreferences(updated);
      toast.success("Notification preferences saved.");
    } catch (error) { toast.error(error.message); } finally { setSaving(false); }
  };

  if (!preferences) return <div className="px-6 py-16 text-center text-sm text-[#777]">Loading notification preferences…</div>;
  const set = (key, value) => setPreferences((current) => ({ ...current, [key]: value }));

  return <div className="mx-auto max-w-5xl px-6 py-10 sm:px-10">
    <div className="mb-8"><div className="overline mb-3">Stay accountable</div><h2 className="font-serif-editorial text-4xl">Notification preferences.</h2><p className="mt-3 max-w-2xl text-sm text-[#A1A1AA]">Choose the reminders that help you follow through. Your choices apply to both the in-app inbox and device push.</p></div>
    <section className="mb-6 border border-white/10 p-5">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center"><div><div className="font-serif-editorial text-xl">Device push notifications</div><p className="mt-1 text-xs text-[#A1A1AA]">Receive alerts even when HustlrMate is not open. Requires browser permission and HTTPS.</p></div><button type="button" onClick={togglePush} disabled={saving || !push.supported || push.permission === "denied"} className="shrink-0 bg-[#F59E0B] px-4 py-2.5 text-xs font-semibold text-black disabled:cursor-not-allowed disabled:opacity-40">{push.subscribed ? "Disable on this device" : push.permission === "denied" ? "Blocked in browser" : "Enable on this device"}</button></div>
      {!push.supported && <p className="mt-3 text-xs text-amber-300">This browser or connection does not support web push. On iPhone or iPad, add HustlrMate to the Home Screen and open it there first.</p>}
    </section>
    <section className="border border-white/10">
      {preferenceRows.map(([key, title, description]) => <label key={key} className="flex cursor-pointer items-center gap-4 border-b border-white/[0.07] px-5 py-4 last:border-0"><div className="min-w-0 flex-1"><div className="text-sm text-[#F5F5F0]">{title}</div><div className="mt-1 text-xs font-normal text-[#777]">{description}</div></div><input type="checkbox" checked={preferences[key]} onChange={(event) => set(key, event.target.checked)} className="h-4 w-4 accent-[#F59E0B]" /></label>)}
      <div className="flex items-center gap-4 border-t border-white/[0.07] bg-white/[0.02] px-5 py-4"><div className="min-w-0 flex-1"><div className="flex items-center gap-2 text-sm text-[#F5F5F0]">Platform announcements <Lock className="h-3.5 w-3.5 text-[#777]" /></div><div className="mt-1 text-xs text-[#777]">Important service and community notices from HustlrMate cannot be disabled.</div></div><input type="checkbox" checked readOnly disabled className="h-4 w-4 accent-[#F59E0B]" /></div>
    </section>
    <section className="mt-6 grid gap-4 border border-white/10 p-5 sm:grid-cols-2">
      <NumberPreference label="Remind me before a task" value={preferences.taskReminderMinutes} min={5} max={180} suffix="minutes" onChange={(value) => set("taskReminderMinutes", value)} />
      <NumberPreference label="Remind me before a meet" value={preferences.meetingReminderMinutes} min={5} max={360} suffix="minutes" onChange={(value) => set("meetingReminderMinutes", value)} />
      <NumberPreference label="Send my daily summary" value={preferences.dailySummaryHoursBeforeEnd} min={1} max={12} suffix="hours before day end" onChange={(value) => set("dailySummaryHoursBeforeEnd", value)} />
      <label className="grid gap-2 text-xs text-[#A1A1AA]">Fallback day-end time<input type="time" value={(preferences.fallbackDayEnd || "22:00").slice(0, 5)} onChange={(event) => set("fallbackDayEnd", event.target.value)} className="border border-white/10 bg-black px-3 py-2.5 text-sm text-white" /><span className="font-normal text-[#666]">Used when your profile has no availability end time.</span></label>
    </section>
    <button type="button" onClick={save} disabled={saving} className="mt-6 bg-[#F59E0B] px-5 py-3 text-xs font-semibold text-black disabled:opacity-50">{saving ? "Saving…" : "Save preferences"}</button>
  </div>;
}

function NumberPreference({ label, value, min, max, suffix, onChange }) {
  return <label className="grid gap-2 text-xs text-[#A1A1AA]">{label}<div className="flex items-center gap-2"><input type="number" min={min} max={max} value={value} onChange={(event) => onChange(Math.max(min, Math.min(max, Number(event.target.value))))} className="w-24 border border-white/10 bg-black px-3 py-2.5 text-sm text-white" /><span className="text-xs font-normal text-[#777]">{suffix}</span></div></label>;
}

function ThemesSettings() {
  const user = getUser();
  const winterEligible = isWinterArcEligible(user);
  const [selected, setSelected] = useState(() => getThemePreference(user));

  const selectTheme = (theme) => {
    const nextTheme = setThemePreference(user, theme);
    setSelected(nextTheme);
    toast(nextTheme === WINTER_ARC_THEME ? "Winter Arc theme applied." : "Default theme applied.");
  };

  return (
    <div className="px-6 sm:px-10 py-10 max-w-5xl mx-auto">
      <div className="mb-8">
        <div className="overline mb-3">Appearance</div>
        <h2 className="font-serif-editorial text-4xl">Choose your theme.</h2>
        <p className="text-sm text-[#A1A1AA] mt-3 max-w-2xl">
          Your choice is saved to this account and applied across the platform.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <ThemeCard
          name="Default"
          description="The original HustlrMate experience with warm orange accents."
          selected={selected === DEFAULT_THEME}
          onSelect={() => selectTheme(DEFAULT_THEME)}
          previewClass="bg-[#0A0A0A] border-white/10"
          accentClass="theme-preview-default-accent"
        />
        {winterEligible && (
          <ThemeCard
            name="Winter Arc"
            description="A strict, minimalist black-and-white experience for Winter Arc members."
            selected={selected === WINTER_ARC_THEME}
            onSelect={() => selectTheme(WINTER_ARC_THEME)}
            previewClass="bg-black border-white/40"
            accentClass="bg-white"
          />
        )}
      </div>
    </div>
  );
}

function ThemeCard({ name, description, selected, onSelect, previewClass, accentClass }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={`text-left p-5 border transition-colors ${selected ? "border-[#F59E0B]" : "border-white/10 hover:border-white/30"}`}
    >
      <div className={`h-36 border p-4 mb-5 ${previewClass}`} aria-hidden="true">
        <div className="flex gap-2 mb-5"><span className={`w-6 h-6 ${accentClass}`} /><span className="h-2 w-20 bg-white/50 mt-2" /></div>
        <div className="grid grid-cols-[30%_1fr] gap-3 h-20">
          <div className="border border-white/15" />
          <div className="border border-white/15 p-3 space-y-2"><div className={`h-2 w-1/2 ${accentClass}`} /><div className="h-2 w-full bg-white/10" /><div className="h-2 w-3/4 bg-white/10" /></div>
        </div>
      </div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-serif-editorial text-xl">{name}</div>
          <p className="text-xs text-[#A1A1AA] mt-1 leading-relaxed">{description}</p>
        </div>
        <span className={`w-6 h-6 shrink-0 border flex items-center justify-center ${selected ? "bg-[#F59E0B] border-[#F59E0B] text-black" : "border-white/20"}`}>
          {selected && <Check className="w-4 h-4" strokeWidth={2.5} />}
        </span>
      </div>
    </button>
  );
}
